import { clsx } from 'clsx';
import { Dealer, Doctype } from '@types';
import { Button, Checkbox, Modal } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

import { dealerHttp } from '@network';
import { useNotify } from 'react-admin';
import { useEffect, useState } from 'react';
import cls from './CreditDocuments.module.css';

type DealerDoctypeFlag = 'PFAE' | 'PM' | 'guarantorPFAE' | 'guarantorPM';


interface Props {
  dealer: Dealer;
  editable: boolean;
  refresh: () => void;
}

export const CreditDocuments = ({ dealer, editable, refresh }: Props) => {
  const [isGuarantor, setIsGuarantor] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [docs, setDocs] = useState<Doctype[]>([]);
  const notify = useNotify();

  const transform = (doc: Doctype): Doctype => {
    if (!doc.dealerDoctype) {
      doc.dealerDoctype = {
        id: null,
        dealerId: dealer.id,
        doctypeId: doc.id,
        PFAE: false,
        PM: false,
        guarantorPFAE: false,
        guarantorPM: false,
      };
    }
    return doc;
  };

  const getDoctypes = async () => {
    try {
      const res = await dealerHttp.getSelectionDoctypes(dealer.id);
      setDocs(res.map(transform));
    } catch (e) {
      notify('Doctypes load is failed.');
      console.error(e);
    }
  };

  const onSubmit = async () => {
    const data: Doctype[] = docs.map((doc: Doctype) => {
      if (doc.dealerDoctype?.id === null && !doc.dealerDoctype.PM && !doc.dealerDoctype.PFAE
        && !doc.dealerDoctype.guarantorPM && !doc.dealerDoctype.guarantorPFAE) {
        doc.dealerDoctype = null;
      }
      return doc;
    });

    try {
      await dealerHttp.setSelectionDoctypes({ dealerId: dealer.id, data });
      notify('Doctypes saved successfully.');
      await onCancel();
    } catch (e) {
      notify('Doctypes save is failed.');
      console.error(e);
    }
  };

  const onCancel = async () => {
    await getDoctypes();
    setIsGuarantor(false);
    setIsModalOpen(false);
  };

  const onCheck = (id: number, field: DealerDoctypeFlag) => {
    setDocs((prev: Doctype[]) => prev.map((doc: Doctype) => {
      if (doc.id === id) {
        const dealerDoctype = doc.dealerDoctype;

        return {
          ...doc,
          dealerDoctype: {
            ...dealerDoctype,
            id: dealerDoctype?.id ?? null,
            dealerId: dealerDoctype?.dealerId ?? dealer.id,
            doctypeId: dealerDoctype?.doctypeId ?? doc.id,
            PFAE: dealerDoctype?.PFAE ?? false,
            PM: dealerDoctype?.PM ?? false,
            guarantorPFAE: dealerDoctype?.guarantorPFAE ?? false,
            guarantorPM: dealerDoctype?.guarantorPM ?? false,
            [field]: !(dealerDoctype?.[field] ?? false),
          },
        };
      }
      return doc;
    }));
  };

  useEffect(() => {
    (async () => {
      await getDoctypes();
    })();
  }, []);

  return (
    <div className={cls.wrap}>
      <button
        className={cls.btn}
        type="button"
        onClick={() => setIsModalOpen(true)}
      ><FileTextOutlined className={cls.btnIcon} />Credit Docs</button>

      <Modal
        title={null}
        open={isModalOpen}
        onOk={onSubmit}
        onCancel={onCancel}
        closable={false}
        maskClosable={false}
        footer={null}
        width={600}
        centered
        wrapClassName={cls.wrapModal}
      >
        <div className={cls.header}>
          <div className={cls.headerTitle}>Credit Docs</div>
          <div className={cls.headerTabs}>
            <button
              onClick={() => setIsGuarantor(false)}
              className={clsx(cls.headerTab, !isGuarantor && cls.active)}>Client</button>
            <button
              onClick={() => setIsGuarantor(true)}
              className={clsx(cls.headerTab, isGuarantor && cls.active)}>Guarantor</button>
          </div>
        </div>
        <div className={cls.body}>
          <div className={cls.bodyTop}>
            {'Documents this partner will request for its clients\' credits. Only documents with "For B2B Clients" on are listed.'}
          </div>
          <div className={cls.bodyForm}>
            <div className={cls.formWrap}>
              <div className={cls.formHeader}>
                <div className={cls.formRow}>
                  <div className={cls.formCol}>Document</div>
                  <div className={cls.formCol}>PFAE</div>
                  <div className={cls.formCol}>PM</div>
                </div>
              </div>
              <div className={cls.formBody}>
                {docs.length ? docs.map((item, i) => (
                  <div className={cls.formRow} key={i}>
                    <div className={cls.formCol}>{item.nameEn}</div>
                    <div className={cls.formCol}>
                      {item.requirements === 'company' ? '—' : (
                        <Checkbox
                          checked={isGuarantor
                            ? item?.dealerDoctype?.guarantorPFAE
                            : item?.dealerDoctype?.PFAE}
                          onChange={() => onCheck(item.id, isGuarantor ? 'guarantorPFAE' : 'PFAE')}/>
                      )}
                    </div>
                    <div className={cls.formCol}>
                      {item.requirements === 'individual' ? '—' : (
                        <Checkbox
                          checked={isGuarantor
                            ? item?.dealerDoctype?.guarantorPM
                            : item?.dealerDoctype?.PM}
                          onChange={() => onCheck(item.id, isGuarantor ? 'guarantorPM' : 'PM')}/>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className={cls.formEmptyRow}>
                    Documents list is empty
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={cls.bodyBottom}>
            {'— = the document does not apply to that type of user (set in Risk → Documents → "Applies to type of User").'}
          </div>
        </div>
        <div className={cls.footer}>
          <Button onClick={onCancel} className={clsx(cls.btn, cls.btnCancel)}>Cancel</Button>
          <Button
            onClick={onSubmit}
            className={clsx(cls.btn, cls.btnSubmit)}
          >Save</Button>
        </div>
      </Modal>
    </div>
  );
};
